import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Loader2, ArrowLeft, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate("/auth/login", { replace: true }), 3500);
      return () => clearTimeout(t);
    }
  }, [done, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!token) {
      setFormError("This reset link is missing its token. Please request a new one.");
      return;
    }
    if (password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFormError("Passwords don't match.");
      return;
    }
    setLoading(true);
    const result = await resetPassword(token, password);
    setLoading(false);
    if (result.success) {
      setDone(true);
    } else {
      setFormError(result.error || "Failed to reset password.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
        data-testid="reset-password-page"
      >
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/05veeg6r_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%282%29.png"
              alt="LocalBest.ai"
              className="h-14 object-contain"
            />
          </Link>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 shadow-2xl backdrop-blur">
          {!done ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
                  Set a new password
                </h2>
                <p className="text-[#94A3B8] text-sm">
                  Choose a strong password (at least 8 characters). You'll be signed in with it
                  right after.
                </p>
              </div>

              {!token && (
                <div className="mb-5 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg flex items-start gap-2">
                  <ShieldAlert className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <p className="text-[#FBBF24] text-sm">
                    This page needs a reset token. Please use the link from your email, or
                    <Link to="/auth/forgot-password" className="underline ml-1">request a new one</Link>.
                  </p>
                </div>
              )}

              {formError && (
                <div className="mb-5 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg">
                  <p className="text-[#EF4444] text-sm" data-testid="reset-error">{formError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">New password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="pl-11 pr-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                      data-testid="reset-new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Confirm new password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-enter your new password"
                      className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full h-12 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#8B5CF6] hover:to-[#F472B6] text-white font-semibold text-base disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Resetting password…</>
                  ) : (
                    "Reset password"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-success-state">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-3">
                Password updated!
              </h2>
              <p className="text-[#94A3B8] text-sm">
                You'll be redirected to the sign-in page in a few seconds.
              </p>
              <Link
                to="/auth/login"
                className="mt-5 inline-block text-[#7C3AED] hover:text-[#8B5CF6] font-medium text-sm"
              >
                Sign in now →
              </Link>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
